import { useMemo } from 'react';
import { useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// Utils
import { createAnimationStyles } from '@/utils';

// Constants
import {
  ANIMATION_SPRING_CONFIGS,
  INTERPOLATE_0_1_CLAMP_BOTH,
} from '@/constants';

// Types
import { AnimationPreset } from '@/types';

interface AnimatedTextProps {
  text: string;
  className?: string;
  startFrame?: number;
  staggerFrames?: number;
  springPreset?: AnimationPreset;
}

export const AnimatedText = ({
  text,
  className = '',
  startFrame = 0,
  staggerFrames = 2,
  springPreset = AnimationPreset.BOUNCY,
}: AnimatedTextProps): React.ReactNode => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Split text into words, keeping track of character indices
  const words = useMemo(() => {
    const result: { word: string; charStartIndex: number }[] = [];
    let charIndex = 0;

    text.split(' ').forEach((word, wordIndex) => {
      if (wordIndex > 0) {
        // Add space before word (except first word)
        result.push({ word: ' ', charStartIndex: charIndex });
        charIndex += 1;
      }
      result.push({ word, charStartIndex: charIndex });
      charIndex += word.length;
    });

    return result;
  }, [text]);

  return (
    <span className={className}>
      {words.map(({ word, charStartIndex }, wordIndex) => {
        // Space handling
        if (word === ' ') {
          const charStartFrame = startFrame + charStartIndex * staggerFrames;
          const progress = spring({
            frame: frame - charStartFrame,
            fps,
            config: ANIMATION_SPRING_CONFIGS[springPreset],
          });
          const opacity = interpolate(progress, ...INTERPOLATE_0_1_CLAMP_BOTH);

          return (
            <span
              key={`space-${wordIndex}`}
              style={{
                opacity,
                display: 'inline',
              }}
            >
              {'\u00A0'}
            </span>
          );
        }

        // Word handling - wrap characters in a non-breaking container
        return (
          <span
            key={`word-${wordIndex}`}
            className="inline-flex whitespace-nowrap"
          >
            {word.split('').map((char, charIndex) => {
              const globalCharIndex = charStartIndex + charIndex;
              const charStartFrame =
                startFrame + globalCharIndex * staggerFrames;
              const progress = spring({
                frame: frame - charStartFrame,
                fps,
                config: ANIMATION_SPRING_CONFIGS[springPreset],
              });

              const opacity = interpolate(
                progress,
                ...INTERPOLATE_0_1_CLAMP_BOTH,
              );

              return (
                <span
                  key={`${char}-${globalCharIndex}`}
                  style={createAnimationStyles.characterReveal({
                    scale: progress,
                    opacity,
                  })}
                >
                  {char}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
};
